// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { AppContext } from '../app';
import { el } from '../dom';
import { formatMass, formatNumber, formatPct, formatYear } from '../format';
import { latestYearTotal, rollingChange, perCapitaByState } from '../analysis';
import { divisionColor, stateColor } from '../colors';

export function renderOverview(root: HTMLElement, ctx: AppContext) {
  const { data } = ctx;
  const { meta, yearTotals, topByYear, insights } = data;

  const totalLifetime = meta.air_total_kg + meta.water_total_kg + meta.land_total_kg;
  const latestTotal = latestYearTotal(yearTotals);
  const rolling = rollingChange(yearTotals, 5);
  const topYearLatest = topByYear[String(meta.latest_year)] ?? [];

  root.appendChild(el('div', { class: 'lede' }, [
    el('h1', null, 'Every industrial polluter in Australia, every substance, every year.'),
    el('p', { html: 'A searchable, mappable, drill-down view of the <span class="glossary-link" data-term="npi" tabindex="0">National Pollutant Inventory</span> — 25 years of <span class="glossary-link" data-term="emission" tabindex="0">emissions</span> from mines, factories, power stations, refineries, breweries and abattoirs, all on one page.' })
  ]));

  // KPI row
  const kpis = el('div', { class: 'kpi-row' });
  kpis.appendChild(kpi('Reporting facilities', formatNumber(meta.reporting_facility_count), `of ${formatNumber(meta.facility_count)} on the register`, 'in ' + formatYear(meta.latest_year)));
  kpis.appendChild(kpi('Substances tracked', formatNumber(meta.substance_count), 'chemicals and pollutants', 'across air, water and land'));
  kpis.appendChild(kpi('Lifetime emissions', formatMass(totalLifetime), 'air + water + land', `${formatYear(meta.earliest_year)}–${formatYear(meta.latest_year)}`));
  kpis.appendChild(kpi(`Latest year total (${formatYear(meta.latest_year)})`, formatMass(latestTotal), rolling != null ? `${formatPct(rolling)} vs prior 5-year average` : 'most recent reporting year'));
  root.appendChild(kpis);

  // Two-column: latest top 10 + breakdown by medium
  const grid = el('div', { class: 'grid-2' });

  // Top facilities (latest year)
  const top = el('div', { class: 'card' });
  top.appendChild(el('h2', { class: 'card-title' }, [`Top emitters in ${formatYear(meta.latest_year)}`]));
  top.appendChild(el('p', { class: 'card-subtitle' }, 'Largest single-year total release (air + water + land). Click a row to drill in.'));
  const topRows = topYearLatest.slice(0, 10);
  if (topRows.length === 0) {
    top.appendChild(el('p', { class: 'text-tertiary' }, 'No data available for the latest year yet.'));
  } else {
    top.appendChild(buildTopTable(topRows, ctx));
  }
  grid.appendChild(top);

  // Medium breakdown
  const medium = el('div', { class: 'card' });
  medium.appendChild(el('h2', { class: 'card-title' }, 'Lifetime emissions by medium'));
  medium.appendChild(el('p', { class: 'card-subtitle' }, 'Air dominates by tonnes — but the most concerning persistent toxics often travel in water and land transfers.'));
  medium.appendChild(buildMediumBars(meta));
  medium.appendChild(el('div', { class: 'divider' }));

  medium.appendChild(el('h3', { class: 'card-title' }, [
    'Per-capita ranking by ',
    el('span', { class: 'glossary-link', 'data-term': 'per_capita', tabindex: '0' }, 'state')
  ]));
  medium.appendChild(el('p', { class: 'card-subtitle' }, 'Total lifetime emissions per 1,000 residents (ABS 2024 ERP).'));
  medium.appendChild(buildPerCapita(meta));
  grid.appendChild(medium);

  root.appendChild(grid);

  // Persistent emitters panel
  if (insights.persistent.length > 0) {
    const p = el('div', { class: 'card mt-lg' });
    p.appendChild(el('h2', { class: 'card-title' }, 'Persistent top-10 emitters'));
    p.appendChild(el('p', { class: 'card-subtitle' }, `Facilities that appeared in the national top-10 in four or more of the last five reporting years (${formatYear(insights.latest_year - 4)} – ${formatYear(insights.latest_year)}).`));
    const list = el('div', { class: 'table-wrap' });
    const table = el('table', { class: 'data' }, [
      el('thead', null, [
        el('tr', null, [
          el('th', null, 'Facility'),
          el('th', null, 'Industry'),
          el('th', null, 'State'),
          el('th', null, 'Years in top 10'),
          el('th', null, '5-year total')
        ])
      ]),
      el('tbody', null,
        insights.persistent.slice(0, 12).map(p => el('tr', {
          class: 'clickable',
          onclick: () => ctx.openFacility(p.id)
        }, [
          el('td', null, p.name),
          el('td', { class: 'text-secondary' }, p.anzsic_class_name),
          el('td', null, [pillState(p.state)]),
          el('td', { class: 'num' }, String(p.count)),
          el('td', { class: 'num' }, formatMass(p.total))
        ]))
      )
    ]);
    list.appendChild(table);
    p.appendChild(list);
    root.appendChild(p);
  }
}

function kpi(label: string, value: string, foot1: string, foot2?: string): HTMLElement {
  return el('div', { class: 'kpi' }, [
    el('div', { class: 'kpi-label' }, label),
    el('div', { class: 'kpi-value' }, value),
    el('div', { class: 'kpi-foot' }, [
      foot1,
      foot2 ? el('br') : null,
      foot2 ?? ''
    ])
  ]);
}

function pillState(state: string | null): HTMLElement {
  if (!state) return el('span', { class: 'pill' }, '—');
  return el('span', {
    class: 'pill pill-state',
    style: `background:${stateColor(state)}22;color:${stateColor(state)};border-color:${stateColor(state)}40`
  }, state);
}

function buildTopTable(rows: import('../types').TopByYearEntry[], ctx: AppContext): HTMLElement {
  const max = rows[0].total;
  const wrap = el('div', { class: 'table-wrap' });
  const t = el('table', { class: 'data' }, [
    el('thead', null, [
      el('tr', null, [
        el('th', null, '#'),
        el('th', null, 'Facility'),
        el('th', null, 'State'),
        el('th', null, 'Total release'),
        el('th', { style: 'width:120px' }, '')
      ])
    ]),
    el('tbody', null,
      rows.map((r, i) => el('tr', { class: 'clickable', onclick: () => ctx.openFacility(r.id) }, [
        el('td', { class: 'num' }, String(i + 1)),
        el('td', null, [
          el('div', null, r.name),
          el('div', { class: 'text-tertiary', style: 'font-size: var(--font-size-xs)' }, [r.suburb || '—', ' · ', r.anzsic_class_name])
        ]),
        el('td', null, [pillState(r.state)]),
        el('td', { class: 'num' }, formatMass(r.total)),
        el('td', null, [buildBar(r.total / max, divisionColor(r.division_code))])
      ]))
    )
  ]);
  wrap.appendChild(t);
  return wrap;
}

function buildBar(frac: number, color: string): HTMLElement {
  const w = Math.max(2, Math.min(100, frac * 100));
  return el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 8px; width: 100%; overflow: hidden;' }, [
    el('div', { style: `background: ${color}; height: 100%; width: ${w}%; transition: width 300ms ease;` })
  ]);
}

function buildMediumBars(meta: import('../types').Meta): HTMLElement {
  const total = meta.air_total_kg + meta.water_total_kg + meta.land_total_kg;
  const items = [
    { label: 'Air', kg: meta.air_total_kg, color: 'var(--air)' },
    { label: 'Water', kg: meta.water_total_kg, color: 'var(--water)' },
    { label: 'Land', kg: meta.land_total_kg, color: 'var(--land)' }
  ];
  return el('div', { style: 'display: flex; flex-direction: column; gap: var(--space-md);' },
    items.map(i => {
      const pct = (i.kg / total) * 100;
      return el('div', null, [
        el('div', { style: 'display: flex; justify-content: space-between; font-size: var(--font-size-sm); margin-bottom: 4px' }, [
          el('span', null, i.label),
          el('span', { class: 'text-mono text-secondary' }, [formatMass(i.kg), ' · ', pct.toFixed(1) + '%'])
        ]),
        el('div', { style: 'background: var(--bg-elevated); border-radius: 6px; height: 12px; overflow: hidden;' }, [
          el('div', { style: `background: ${i.color}; height: 100%; width: ${Math.max(0.5, pct)}%;` })
        ])
      ]);
    })
  );
}

function buildPerCapita(meta: import('../types').Meta): HTMLElement {
  const items = perCapitaByState(meta);
  const max = items[0]?.perCapita ?? 1;
  return el('div', { style: 'display: flex; flex-direction: column; gap: var(--space-sm);' },
    items.map(i => el('div', { style: 'display: grid; grid-template-columns: 50px 1fr 100px; gap: var(--space-sm); align-items: center;' }, [
      pillState(i.state),
      el('div', { style: 'background: var(--bg-elevated); border-radius: 4px; height: 10px; overflow: hidden;' }, [
        el('div', { style: `background: ${stateColor(i.state)}; height: 100%; width: ${(i.perCapita / max) * 100}%;` })
      ]),
      el('span', { class: 'text-mono', style: 'font-size: var(--font-size-xs); text-align:right' }, formatMass(i.perCapita) + '/k')
    ]))
  );
}
